import Config from "src/config";
import { Subscription, UserManagement } from "src/interfaces";
import { AdminAppSettings } from "src/interfaces/AppSettings";
import { Complaints } from "src/interfaces/Complaints";
import { FileType } from "src/interfaces/KnowledgeBase";
import {
  DownloadDoc,
  FileValidate,
  UploadFile,
} from "../documents/documents.api.types";
import { ResponseKind } from "../MainService";
import { api, Api } from "../MainService/api";
import { ErrorKind } from "../MainService/apiProblem";

const ADMIN_URL = Config.ADMIN_URL;
const DOCUMENTS_URL = `${ADMIN_URL}/knowledge-base`;
export class AdminApi {
  private api: Api;

  constructor(api: Api) {
    this.api = api;
  }

  async updateConfig(request: any) {
    try {
      const response = await this.api.apisauce.put(
        `${ADMIN_URL}/config`,
        request
      );

      let data = await this.api.getResponse(response);

      return data;
    } catch (e) {
      // alert("You entered some bad data")
      return { kind: ErrorKind.BAD_DATA_ERROR, errors: "Bad Data" };
    }
  }

  async getConfig() {
    try {
      const response = await this.api.apisauce.get<AdminAppSettings>(
        `${ADMIN_URL}/config`
      );
      let data = await this.api.getResponse(response, true);
      return data;
    } catch (e) {
      console.log(e);
      return { kind: ErrorKind.BAD_DATA_ERROR };
    }
  }

  async getComplaints() {
    try {
      const response = await this.api.apisauce.get<Complaints>(
        `${ADMIN_URL}/list-complaints`
      );
      let data = await this.api.getResponse(response, true);
      return data;
    } catch (e) {
      console.log(e);
      return { kind: ErrorKind.BAD_DATA_ERROR };
    }
  }

  async resolveComplaints(id: number) {
    try {
      const response = await this.api.apisauce.put(
        `${ADMIN_URL}/update-complaint-status?complaint_id=${id}&status=resolved`
      );
      let data = await this.api.getResponse(response, true);
      return data;
    } catch (e) {
      console.log(e);
      return { kind: ErrorKind.BAD_DATA_ERROR };
    }
  }

  async uploadKnowledgebaseDoc(files: File[]) {
    try {
      const formData = new FormData();

      files.forEach((file) => {
        formData.append("files", file);
      });

      const response = await this.api.apisauce.post<UploadFile>(
        `${DOCUMENTS_URL}/upload`,
        formData
      );

      let data = await this.api.getResponse(response);

      return data;
    } catch (e) {
      // alert("You entered some bad data")
      return { kind: ErrorKind.BAD_DATA_ERROR, errors: "Bad Data" };
    }
  }

  async uploadKnowledgebaseGdrive(gDriveLink: string) {
    try {
      const response = await this.api.apisauce.post<UploadFile>(
        `${DOCUMENTS_URL}/upload-gdrive?gdrivelink=${gDriveLink}`
      );

      let data = await this.api.getResponse(response);

      return data;
    } catch (e) {
      // alert("You entered some bad data")
      return { kind: ErrorKind.BAD_DATA_ERROR, errors: "Bad Data" };
    }
  }

  async getUserTokenKnowledgebase() {
    try {
      const response = await this.api.apisauce.get<{
        azure_storage_token: string;
      }>(`${DOCUMENTS_URL}/get-azure-storage-token`);
      let data = await this.api.getResponse(response, true);
      return data;
    } catch (e) {
      console.log(e);
      return { kind: ErrorKind.BAD_DATA_ERROR };
    }
  }

  async downloadKnowledgebaseDoc(id: string) {
    try {
      const response = await this.api.apisauce.get<DownloadDoc>(
        `${DOCUMENTS_URL}/download/${id}`
      );

      let data = await this.api.getResponse(response);

      return data;
    } catch (e) {
      // alert("You entered some bad data")
      return { kind: ErrorKind.BAD_DATA_ERROR, errors: "Bad Data" };
    }
  }

  async getAzureStorageUrl(name: string) {
    try {
      const response = await this.api.apisauce.get<{
        upload_url: string;
      }>(`${DOCUMENTS_URL}/get-azure-storage-upload-url/${name}`);
      let data = await this.api.getResponse(response, true);
      return data;
    } catch (e) {
      console.log(e);
      return { kind: ErrorKind.BAD_DATA_ERROR };
    }
  }

  async uploadKnowledgeDoc(
    file: File,
    level: string,
    file_type: FileType,
    subject: string = ""
  ) {
    try {
      let is_question_bank = false;
      let azure_token_url = "";

      if (file_type === "Question Bank") is_question_bank = true;

      const token = await this.getAzureStorageUrl(file.name);

      if (token.kind == ResponseKind.OK) {
        if (token.data?.upload_url) azure_token_url = token.data.upload_url;
      } else {
        console.log("Error encountered getting Azure URL");
        return false;
      }

      const config = {
        headers: {
          "x-ms-blob-type": "BlockBlob",
          "Content-Type": file.type,
          "x-ms-meta-is_question_bank": `${is_question_bank}`,
          "x-ms-meta-level": `${level}`,
          ...(subject && { "x-ms-meta-subject": subject }), // Add subject to metadata if provided
        },
      };
      const response = await this.api.apisauce.put(
        azure_token_url,
        file,
        config
      );
      console.log("Uploaded file in api ", response);
      if (!response.ok) {
        return false;
      } else {
        return true;
      }
    } catch (e) {
      console.log("File uploading failed");
      Promise.reject("Bad api call");
    }
  }

  async validateFiles(fileNames: string[]) {
    try {
      const response = await this.api.apisauce.post<{ files: FileValidate[] }>(
        `${DOCUMENTS_URL}/validate-documents`,
        {
          file_names: fileNames,
        }
      );
      let data = await this.api.getResponse(response);

      return data;
    } catch (e) {
      // alert("You entered some bad data")
      return { kind: ErrorKind.BAD_DATA_ERROR, errors: "Bad Data" };
    }
  }

  async getUsers() {
    try {
      const response = await this.api.apisauce.get<{ users: UserManagement[] }>(
        `${ADMIN_URL}/list-users`
      );
      let data = await this.api.getResponse(response, true);
      return data;
    } catch (e) {
      console.log(e);
      return { kind: ErrorKind.BAD_DATA_ERROR };
    }
  }

  async getUserwithPaymentHistory() {
    try {
      const response = await this.api.apisauce.get<Subscription[]>(
        `${ADMIN_URL}/list-users-with-payment-history`
      );
      let data = await this.api.getResponse(response, true);
      return data;
    } catch (e) {
      console.log(e);
      return { kind: ErrorKind.BAD_DATA_ERROR };
    }
  }

  async changeUserStatus(id: number) {
    try {
      const response = await this.api.apisauce.get(
        `${ADMIN_URL}/change-account-status?user_id=${id}`
      );
      let data = await this.api.getResponse(response, true);
      return data;
    } catch (e) {
      console.log(e);
      return { kind: ErrorKind.BAD_DATA_ERROR };
    }
  }

  async promoteUserLevel(username: string) {
    try {
      const response = await this.api.apisauce.post(
        `${ADMIN_URL}/promote-child?child_username=${username}`,
        {}
      );
      const data = await this.api.getResponse(response, true);
      return data;
    } catch (e) {
      console.log(e);
      return { kind: ErrorKind.BAD_DATA_ERROR };
    }
  }

  async updateUserSubjects(username: string, subjectIds: number[]) {
    try {
      const response = await this.api.apisauce.post(
        `${ADMIN_URL}/update-subjects?child_username=${username}`,
        { subject_ids: subjectIds }
      );
      const data = await this.api.getResponse(response, true);
      return data;
    } catch (e) {
      console.log(e);
      return { kind: ErrorKind.BAD_DATA_ERROR };
    }
  }

  async deleteUserAccount(id: number) {
    try {
      const response = await this.api.apisauce.delete(
        `${ADMIN_URL}/delete-user`,
        {},
        { params: { user_id: id } }
      );
      const data = await this.api.getResponse(response, true);
      return data;
    } catch (e) {
      console.log(e);
      return { kind: ErrorKind.BAD_DATA_ERROR };
    }
  }

  async updateUserPassword(id: number, password: string) {
    try {
      const response = await this.api.apisauce.post(
        `${ADMIN_URL}/update-user-password?user_id=${id}`,
        { new_password: password }
      );
      const data = await this.api.getResponse(response, true);
      return data;
    } catch (e) {
      console.log(e);
      return { kind: ErrorKind.BAD_DATA_ERROR };
    }
  }

  async registerParent(
    name: string,
    email: string,
    password: string,
    phone_number: string
  ) {
    try {
      const response = await this.api.apisauce.post(
        `${ADMIN_URL}/register-parent`,
        {
          name,
          email,
          password,
          role: 1,
          phone_number,
        }
      );
      const data = await this.api.getResponse(response, true);
      return data;
    } catch (e) {
      console.log(e);
      return { kind: ErrorKind.BAD_DATA_ERROR };
    }
  }

  async registerChild(formData: FormData, parentEmail: string) {
    try {
      const response = await this.api.apisauce.post(
        `${ADMIN_URL}/register-child?parent_email=${encodeURIComponent(parentEmail)}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      const data = await this.api.getResponse(response, true);
      return data;
    } catch (e) {
      console.log(e);
      return { kind: ErrorKind.BAD_DATA_ERROR };
    }
  }

  async getChildSubjects(childUsername: string) {
    try {
      const response = await this.api.apisauce.get<{
        structure_kb: any;
        structure_qb: any;
        id: number;
        structure_progress: any;
        name: string;
        description: string;
        is_kb_required: boolean;
        is_qb_required: boolean;
      }[]>(
        `${ADMIN_URL}/get-child-subjects?child_username=${encodeURIComponent(
          childUsername
        )}`
      );
      const data = await this.api.getResponse(response, true);
      return data;
    } catch (e) {
      console.log(e);
      return { kind: ErrorKind.BAD_DATA_ERROR };
    }
  }
}

export const adminService = new AdminApi(api);
